import React, { useState, useEffect, useCallback } from 'react';
import { 
  Typography, 
  Paper, 
  Box, 
  Button, 
  CircularProgress, 
  Alert, 
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel
} from '@mui/material';
import { 
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { db } from '../../firebaseConfig';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import './archiveUsersPage.css';

const ArchiveUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showArchivedOnly, setShowArchivedOnly] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    userId: '',
    userName: '',
    action: '', // 'archive' ou 'unarchive'
  });

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  }, []);

  // Charger tous les utilisateurs
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const usersRef = collection(db, 'users');
      const usersSnap = await getDocs(usersRef);
      
      // Traiter chaque utilisateur et récupérer ses enfants
      const userPromises = usersSnap.docs.map(async (doc) => {
        const userData = doc.data();
        // Exclure les administrateurs de la liste
        if (!userData.isAdmin) {
          try {
            // Récupérer les enfants de cet utilisateur
            const childrenRef = collection(db, 'users', doc.id, 'children');
            const childrenSnap = await getDocs(childrenRef);
            
            const children = [];
            childrenSnap.forEach((childDoc) => {
              const childData = childDoc.data();
              children.push({
                id: childDoc.id,
                name: childData.name || '',
                firstName: childData.firstName || '',
                class: childData.class || ''
              });
            });

            return {
              id: doc.id,
              ...userData,
              isArchived: userData.isArchived || false,
              children: children
            };
          } catch (error) {
            console.error(`Erreur lors de la récupération des enfants pour l'utilisateur ${doc.id}:`, error);
            return {
              id: doc.id,
              ...userData,
              isArchived: userData.isArchived || false,
              children: []
            };
          }
        }
        return null;
      });

      const resolvedUsers = await Promise.all(userPromises);
      const filteredUsers = resolvedUsers.filter(user => user !== null);

      setUsers(filteredUsers);
      setFilteredUsers(filteredUsers);
    } catch (error) {
      console.error('Erreur lors du chargement des utilisateurs:', error);
      showSnackbar('Erreur lors du chargement des utilisateurs', 'error');
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  // Filtrer les utilisateurs
  useEffect(() => {
    let filtered = users;

    // Filtrer par terme de recherche
    if (searchTerm) {
      filtered = filtered.filter(user => {
        const searchLower = searchTerm.toLowerCase();
        
        // Recherche dans les informations de l'utilisateur
        const userMatch = user.email?.toLowerCase().includes(searchLower) ||
          user.displayName?.toLowerCase().includes(searchLower) ||
          user.firstName?.toLowerCase().includes(searchLower) ||
          user.lastName?.toLowerCase().includes(searchLower);
        
        // Recherche dans les noms des enfants
        const childrenMatch = user.children?.some(child => 
          child.name?.toLowerCase().includes(searchLower) ||
          child.firstName?.toLowerCase().includes(searchLower) ||
          child.class?.toLowerCase().includes(searchLower)
        );
        
        return userMatch || childrenMatch;
      });
    }

    // Filtrer par statut d'archivage
    if (showArchivedOnly) {
      filtered = filtered.filter(user => user.isArchived);
    }

    setFilteredUsers(filtered);
  }, [users, searchTerm, showArchivedOnly]);

  const handleArchiveUser = async (userId, isArchived) => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        isArchived: !isArchived,
        archivedDate: !isArchived ? new Date() : null
      });

      // Mettre à jour l'état local
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId 
            ? { ...user, isArchived: !isArchived }
            : user
        )
      );

      const action = !isArchived ? 'archivé' : 'désarchivé';
      showSnackbar(`Utilisateur ${action} avec succès`);
    } catch (error) {
      console.error('Erreur lors de l\'archivage:', error);
      showSnackbar('Erreur lors de l\'archivage de l\'utilisateur', 'error');
    }
  };

  const openConfirmDialog = (userId, userName, action) => {
    setConfirmDialog({
      open: true,
      userId,
      userName,
      action
    });
  };

  const handleConfirmAction = () => {
    const { userId } = confirmDialog;
    const user = users.find(u => u.id === userId);
    if (user) {
      handleArchiveUser(userId, user.isArchived);
    }
    setConfirmDialog({ open: false, userId: '', userName: '', action: '' });
  };

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const getDisplayName = (user) => {
    if (user.displayName) return user.displayName;
    if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`;
    if (user.firstName) return user.firstName;
    return user.email || 'Utilisateur inconnu';
  };

  const getChildrenDisplay = (children) => {
    if (!children || children.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" style={{ fontStyle: 'italic' }}>
          Aucun enfant
        </Typography>
      );
    }

    return (
      <Box>
        {children.map((child, index) => (
          <Typography key={child.id} variant="body2" sx={{ mb: 0.5 }}>
            {child.firstName} {child.name}
            {child.class && (
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                ({child.class})
              </Typography>
            )}
          </Typography>
        ))}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Gestion des utilisateurs archivés
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Vous pouvez archiver les utilisateurs qui ne doivent plus avoir accès à l'application. 
        Les utilisateurs archivés ne pourront plus se connecter et leurs enfants n'apparaîtront plus dans les autres pages d'administration.
      </Typography>

      {/* Barre d'outils */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder="Rechercher par nom, email ou nom d'enfant..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ minWidth: 300 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />
          
          <FormControlLabel
            control={
              <Switch
                checked={showArchivedOnly}
                onChange={(e) => setShowArchivedOnly(e.target.checked)}
              />
            }
            label="Afficher uniquement les archivés"
          />
          
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchUsers}
          >
            Actualiser
          </Button>
        </Box>
      </Paper>

      {/* Tableau des utilisateurs */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nom</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Enfants</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="text.secondary">
                    {searchTerm || showArchivedOnly 
                      ? 'Aucun utilisateur trouvé avec ces critères'
                      : 'Aucun utilisateur trouvé'
                    }
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <Typography variant="body2">
                      {getDisplayName(user)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {user.email || 'Email non disponible'}
                    </Typography>
                  </TableCell>
                  <TableCell className="archive-users-children-cell">
                    {getChildrenDisplay(user.children)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.isArchived ? 'Archivé' : 'Actif'}
                      color={user.isArchived ? 'error' : 'success'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {user.isArchived ? (
                      <IconButton
                        color="primary"
                        onClick={() => openConfirmDialog(user.id, getDisplayName(user), 'unarchive')}
                        title="Désarchiver l'utilisateur"
                      >
                        <UnarchiveIcon />
                      </IconButton>
                    ) : (
                      <IconButton
                        color="error"
                        onClick={() => openConfirmDialog(user.id, getDisplayName(user), 'archive')}
                        title="Archiver l'utilisateur"
                      >
                        <ArchiveIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog de confirmation */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, userId: '', userName: '', action: '' })}
      >
        <DialogTitle>
          Confirmer l'action
        </DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir {confirmDialog.action === 'archive' ? 'archiver' : 'désarchiver'} 
            l'utilisateur <strong>{confirmDialog.userName}</strong> ?
          </Typography>
          {confirmDialog.action === 'archive' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Un utilisateur archivé ne pourra plus se connecter à l'application.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmDialog({ open: false, userId: '', userName: '', action: '' })}
          >
            Annuler
          </Button>
          <Button 
            onClick={handleConfirmAction}
            color={confirmDialog.action === 'archive' ? 'error' : 'primary'}
            variant="contained"
          >
            {confirmDialog.action === 'archive' ? 'Archiver' : 'Désarchiver'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar pour les notifications */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert 
          onClose={() => setOpenSnackbar(false)} 
          severity={snackbarSeverity}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ArchiveUsersPage;
